import { Map } from 'immutable';

import type { BackendPeriod, RecordOfMatchEntity } from './betting-entities';
import {
  BasketballPeriodType,
  ESportsPeriodType,
  HandballPeriodType,
  IceHockeyPeriodType,
  SoccerPeriodType,
  TableTennisPeriodType,
  TennisPeriodType,
  VolleyballPeriodType,
} from './betting-entities';
import {
  MATCH_PERIODS_BY_SPORT,
  MATCH_TOTAL_SCORES_TYPE_BY_SPORT,
  SportId, TeamId,
  TIME_PERIODS_BY_SPORT,
} from './constants';
import { hasPeriodsBySportId, hasScoresBySportId } from './sport-utils';

// ---

export type MainScores = Map<TeamId, number> | null;

export function accessMainScore(entity?: RecordOfMatchEntity): MainScores {
  if (!entity || typeof entity.scores === 'number') {
    return null;
  }

  if (!hasScoresBySportId(entity.sportId) || !hasPeriodsBySportId(entity.sportId)) {
    return null;
  }
  return entity.scores
    ?.get(MATCH_TOTAL_SCORES_TYPE_BY_SPORT[entity.sportId])
    ?.get(MATCH_PERIODS_BY_SPORT[entity.sportId])
    ?.get(0) || null;
}

// ---

// TODO https://jira.tennisi.it/browse/SITE-977

export function accessTimePeriodForSports(
  sportId?: SportId,
  periods?: RecordOfMatchEntity['periods'],
): BackendPeriod['number'] | null {
  if (!periods || !sportId) {
    return null;
  }

  return periods
    .get(TIME_PERIODS_BY_SPORT[sportId])
    ?.get('number') || null;
}

export function accessTimePeriod(entity?: RecordOfMatchEntity): BackendPeriod['number'] | null {
  if (!entity?.periods || !hasPeriodsBySportId(entity.sportId)) {
    return null;
  }

  return entity.periods
    ?.get(TIME_PERIODS_BY_SPORT[entity.sportId])
    ?.get('number') || null;
}

// ---

const sportIdToPeriodMap = {
  [SportId.SOCCER]: SoccerPeriodType.MATCH,
  [SportId.TENNIS]: TennisPeriodType.MATCH,
  [SportId.TABLE_TENNIS]: TableTennisPeriodType.MATCH,
  [SportId.BASKETBALL]: BasketballPeriodType.MATCH,
  [SportId.VOLLEYBALL]: VolleyballPeriodType.MATCH,
  [SportId.ICE_HOCKEY]: IceHockeyPeriodType.MATCH,
  [SportId.DOTA]: ESportsPeriodType.MATCH,
  [SportId.CS_GO]: ESportsPeriodType.MATCH,
  [SportId.E_SPORT]: ESportsPeriodType.MATCH,
  [SportId.HANDBALL]: HandballPeriodType.MATCH,
};

// TODO https://jira.tennisi.it/browse/SITE-977

export function isFinishedSportMatch(
  sportId?: SportId,
  periods?: RecordOfMatchEntity['periods'],
): boolean {
  if (!sportId || !periods){
    return true;
  }

  if (sportId in sportIdToPeriodMap) {
    const matchPeriod = periods.get(
      sportIdToPeriodMap[sportId as keyof typeof sportIdToPeriodMap],
      null,
    );

    return !!matchPeriod && matchPeriod.finished;
  }

  return true;
}

export function isFinishedMatch(entity?: RecordOfMatchEntity): boolean {
  if (!entity) {
    return true;
  }

  if (entity.sportId in sportIdToPeriodMap) {
    const matchPeriod = entity.periods.get(
      sportIdToPeriodMap[entity.sportId as keyof typeof sportIdToPeriodMap],
      null,
    );

    return !!matchPeriod && matchPeriod.finished;
  }

  return true;
}
